import { computed } from "vue";
import { flattenSearchItems } from "#/doctor_finder/omnisearch-quantum-items";

const DEFAULT_PHRASE_GROUP_LABEL = "Search for phrase";

// Search glyph styled like the generic result-row icons (white on the
// themeable default circle).
const PHRASE_PREPEND_ICON = {
  name: "search-bold-filled",
  color: "#fff",
  background: { shape: "circle", color: "var(--sp-comp-omni-search-default-icon-bg, #b2d9dc)" },
};

/**
 * The quantum-select item set for the omnisearch: the store's idle items while
 * the field is empty, otherwise the flattened searchable items. Action rows ride
 * along pinned — bypassing the picker's matching, rendered last, counting toward
 * `max-results`: the "Search for phrase" free-text row (when the embed enables
 * text search), then a chatterbox suggestion for the active query.
 *
 * @param {Object} options
 * @param {Object} options.store - the omnisearch store; reads `omnisearch.value.{searchItems,idleItems}` and `config.value`.
 * @param {import('vue').Ref<string>} options.searchQuery - the debounced query.
 * @param {import('vue').Ref<{ query: string, node: Object }|null>} options.suggestion - the chatterbox suggestion and the query it was fetched for.
 * @returns {{ models: import('vue').ComputedRef<Array<Object>> }}
 */
export function useOmnisearchModels({ store, searchQuery, suggestion }) {
  // Children are flattened to leaves so a matching subspecialty ranks on its own;
  // the picker's `fuzzy-search` does the ranking and capping.
  const flatSearchItems = computed(() => flattenSearchItems(store.omnisearch.value.searchItems));

  // Whether free-text queries are searchable on this finder (the wizard submits
  // the typed text as a `query` filter). Gates the "Search for phrase" row.
  const textSearchEnabled = computed(() => Boolean(store.config?.value?.textSearchEnabled));

  // The "Search for phrase" row: selecting it routes the raw text as a
  // `query` filter through the wizard's `payload.query` branch.
  // `selectionText` keeps the quotes out of the committed field text — the
  // quoted title is display only. `group` renders as the row's section header
  // in both filter paths — pinned rows keep their header even in the flat
  // ranked list (see `withPinnedHeaders` in useQuantumSelectItems).
  function buildPhraseNode(query) {
    return {
      title: `"${query}"`,
      selectionText: query,
      value: `phrase-search|${query}`,
      group: store.config?.value?.phraseSearchGroupLabel ?? DEFAULT_PHRASE_GROUP_LABEL,
      pinned: true,
      payload: { query },
      props: { prependIcon: PHRASE_PREPEND_ICON },
    };
  }

  const models = computed(() => {
    const query = searchQuery.value.trim();
    if (!query) {
      return store.omnisearch.value.idleItems;
    }

    const rows = [...flatSearchItems.value];
    if (textSearchEnabled.value) {
      rows.push(buildPhraseNode(query));
    }
    const current = suggestion.value;
    if (current && current.query === query) {
      rows.push({ ...current.node, pinned: true });
    }
    return rows;
  });

  return { models };
}
